// src/legal/legalFormat.ts
//
// Small helpers shared by the dialog, the list and the PDF.

// Fixed names: Intl abbreviates September as "Sept" in en-GB on newer
// browsers and as "Sep" on older ones.
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

// The date of a legal text is the date where the operator sits, for every
// reader. In the reader's own zone one moment would be the 12th for one and
// the 13th for another, and could contradict the date line in the text.
const LEGAL_TIME_ZONE = 'Europe/Vaduz';

function calendarIn(timeZone: string | undefined) {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });
}

// A browser without that zone in its data (none is known) still has to show a
// date: Zurich keeps the same time, and the reader's own zone is the last
// resort.
const CALENDAR = (() => {
    for (const zone of [LEGAL_TIME_ZONE, 'Europe/Zurich']) {
        try {
            return calendarIn(zone);
        } catch {
            // Try the next one.
        }
    }
    return calendarIn(undefined);
})();

/** Year, month (1 to 12) and day of a moment, in Vaduz. */
function legalDay(ms: number): { year: number; month: number; day: number } {
    const parts = { year: 0, month: 0, day: 0 };
    for (const { type, value } of CALENDAR.formatToParts(new Date(ms))) {
        if (type === 'year' || type === 'month' || type === 'day') {
            parts[type] = Number(value);
        }
    }
    return parts;
}

/** "15 Dec 2026": the day in Vaduz, whatever the reader's time zone. */
export function formatLegalDate(ms: number): string {
    const { year, month, day } = legalDay(ms);
    return `${day} ${MONTHS[month - 1]} ${year}`;
}

/** "2026-12-15": the same day, for a file name. */
export function legalDateStamp(ms: number): string {
    const { year, month, day } = legalDay(ms);
    const two = (value: number) => String(value).padStart(2, '0');
    return `${year}-${two(month)}-${two(day)}`;
}

const LANGUAGE_NAMES: Record<string, string> = {
    en: 'English',
    de: 'Deutsch',
    fr: 'Français',
    it: 'Italiano',
};

/** A language in its own words; the code itself when it is not known. */
export const languageName = (language: string) =>
    LANGUAGE_NAMES[language] ?? language.toUpperCase();

/**
 * "terms-2026-10-12-de.pdf": named by the date the text took effect, as
 * everywhere a user sees it; "terms-de.pdf" when no date is known.
 */
export const legalFileName = (
    id: string,
    effectiveFromMs: number | null,
    language: string,
    extension: 'pdf' | 'md'
) =>
    [
        id,
        ...(effectiveFromMs === null ? [] : [legalDateStamp(effectiveFromMs)]),
        language,
    ].join('-') + `.${extension}`;

/**
 * Which text this is, for the reader: "As of 12 Oct 2026", or "Takes effect
 * 15 Dec 2026" for one that is announced. Users are not shown a version
 * number; that stays in the manifest, the decision and the log. Null when no
 * date is known (the author preview, the bundled fallback).
 */
export function dateLine(
    effectiveFromMs: number | null,
    nowMs: number
): string | null {
    if (effectiveFromMs === null) return null;
    const date = formatLegalDate(effectiveFromMs);
    return effectiveFromMs > nowMs ? `Takes effect ${date}` : `As of ${date}`;
}

/** Hand a file to the browser's download. */
export function saveBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking in the same task can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
